/**
 * Token Refresh Types
 *
 * Centralized type definitions for token refresh operations.
 * These types are used by TokenRefreshService, AuthService, and AuthIntegration.
 *
 * @module types/auth/token
 */

import { API_ENDPOINTS } from '../api.types';

// =============================================================================
// TOKEN REFRESH CONFIG
// =============================================================================

/**
 * Configuration options for token refresh operations.
 *
 * @example
 * ```typescript
 * const config: TokenRefreshConfig = {
 *   baseURL: 'https://api.example.com',
 *   refreshEndpoint: '/auth/refresh',
 *   tokenPrefix: 'Bearer ',
 *   timeout: 10000,
 * };
 * ```
 */
export interface TokenRefreshConfig {
  /**
   * Base URL for the authentication API.
   * If not provided, defaults to the auth base URL from config.
   */
  baseURL?: string;

  /**
   * Endpoint path for token refresh requests.
   * Can include path parameters like '/sanctum/token/{token}'.
   * @default API_ENDPOINTS.REFRESH_TOKEN.path
   */
  refreshEndpoint?: string;

  /**
   * Prefix to add before the token in Authorization header.
   * @default 'Bearer '
   */
  tokenPrefix?: string;

  /**
   * Timeout in milliseconds for refresh requests.
   * @default 10000
   */
  timeout?: number;
}

/**
 * Required version of TokenRefreshConfig with all fields mandatory.
 * Used internally by TokenRefreshService after merging with defaults.
 */
export type RequiredTokenRefreshConfig = Required<TokenRefreshConfig>;

// =============================================================================
// TOKEN REFRESH RESULT
// =============================================================================

/**
 * Result of a token refresh operation.
 *
 * @example
 * ```typescript
 * // Successful refresh
 * const successResult: TokenRefreshResult = {
 *   success: true,
 *   token: 'new-access-token',
 *   shouldRetry: false,
 * };
 *
 * // Failed refresh with retry
 * const retryResult: TokenRefreshResult = {
 *   success: false,
 *   error: 'Network timeout',
 *   shouldRetry: true,
 * };
 *
 * // Failed refresh without retry (e.g., invalid refresh token)
 * const failedResult: TokenRefreshResult = {
 *   success: false,
 *   error: 'Refresh token expired',
 *   shouldRetry: false,
 * };
 * ```
 */
export interface TokenRefreshResult {
  /**
   * Whether the token refresh was successful.
   */
  success: boolean;

  /**
   * The new access token if refresh was successful.
   */
  token?: string;

  /**
   * Error message if refresh failed.
   */
  error?: string;

  /**
   * Whether the refresh should be retried.
   * True for transient errors (network issues), false for permanent failures
   * (invalid refresh token, revoked session).
   */
  shouldRetry: boolean;
}

// =============================================================================
// TOKEN REFRESH STATE
// =============================================================================

/**
 * State tracking for token refresh operations.
 * Used to prevent concurrent refresh attempts and track refresh history.
 *
 * @example
 * ```typescript
 * const state: TokenRefreshState = {
 *   isRefreshing: false,
 *   lastRefreshTime: dayjs().valueOf(),
 *   refreshAttempts: 0,
 *   lastError: null,
 * };
 * ```
 */
export interface TokenRefreshState {
  /**
   * Whether a refresh operation is currently in progress.
   */
  isRefreshing: boolean;

  /**
   * Timestamp of the last successful refresh (milliseconds since epoch).
   * Null if no refresh has been performed yet.
   */
  lastRefreshTime: number | null;

  /**
   * Number of consecutive refresh attempts.
   * Reset to 0 on successful refresh.
   */
  refreshAttempts: number;

  /**
   * Last error message from a failed refresh attempt.
   * Null if last refresh was successful or no refresh attempted.
   */
  lastError: string | null;
}

// =============================================================================
// TOKEN REFRESH FAILURE
// =============================================================================

/**
 * Why a refresh did not produce a new bearer. The distinction that matters is
 * `rejected` against everything else: the API refreshes a bearer that is STILL
 * VALID, so a 401 on the refresh means the bearer is finished and only signing
 * in again helps. Every other reason leaves a bearer that may still be good.
 *
 * - `rejected` — the API answered 401: the bearer is expired or revoked.
 * - `unavailable` — no answer worth trusting: no signal, a timeout, a 5xx.
 * - `unrecognised` — the API did not know the refresh token (200, empty body).
 * - `no-credentials` — nothing stored to refresh with.
 * - `superseded` — the stored bearer changed while the request was in flight
 *   (a sign-out, or another sign-in), so the answer was thrown away.
 */
export type TokenRefreshFailureReason =
  'rejected' | 'unavailable' | 'unrecognised' | 'no-credentials' | 'superseded';

/**
 * A failed refresh that says why, so a caller can tell a finished bearer from
 * a dropped connection.
 */
export class TokenRefreshError extends Error {
  public readonly reason: TokenRefreshFailureReason;

  constructor(reason: TokenRefreshFailureReason, message: string) {
    super(message);
    this.name = 'TokenRefreshError';
    this.reason = reason;
  }
}

// =============================================================================
// BEARER LIFECYCLE
// =============================================================================

/**
 * What `BearerLifecycleService.evaluate()` found, and did.
 *
 * - `inert` — not token auth, or no bearer stored. Nothing to do.
 * - `fresh` — the bearer is not due yet.
 * - `spent` — the bearer is past its expiry. It cannot be refreshed; the caller
 *   discards the session.
 * - `refreshed` — the bearer was due, and was swapped for a new one.
 * - `deferred` — the bearer was due, but the refresh failed for a reason that
 *   leaves it usable. The next evaluation tries again.
 * - `ended` — the API refused the bearer. The session has been ended.
 */
export type BearerVerdict =
  'inert' | 'fresh' | 'spent' | 'refreshed' | 'deferred' | 'ended';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Default configuration for token refresh operations.
 *
 * Note: baseURL is empty string by default as it should be provided
 * at runtime from the application configuration.
 *
 * @example
 * ```typescript
 * const config = {
 *   ...DEFAULT_TOKEN_REFRESH_CONFIG,
 *   baseURL: getAuthBaseUrl(),
 * };
 * ```
 */
export const DEFAULT_TOKEN_REFRESH_CONFIG: RequiredTokenRefreshConfig = {
  baseURL: '',
  refreshEndpoint: API_ENDPOINTS.REFRESH_TOKEN.path,
  tokenPrefix: 'Bearer ',
  timeout: 10000,
};

/**
 * How long before its expiry a bearer becomes due for a refresh.
 *
 * Nothing schedules a refresh: the app asks "is this bearer due?" when it
 * starts and when it returns to the foreground, so the window has to be wide
 * enough that an ordinary user opens the app inside it. Seven days means anyone
 * who opens the app weekly stays signed in, and a bearer is rotated once — the
 * first time the app is opened inside the window.
 *
 * See docs/roadmap/token-refresh-window-and-scheduling.md.
 */
export const BEARER_REFRESH_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
